import client from '../api/client';
import PaginationMeta from '../types/PaginationMeta';

import {
  AddOpenClassOrderParticipantParams,
  CreateOpenClassParams,
  CreateStandaloneOpenClassBookingOrderParams,
  OpenClass,
  OpenClassBookingOrder,
  OpenClassBookingOrderDetails,
  OpenClassBookingOrderSummary,
  OpenClassRegistrationSelectionParams,
  OpenClassRegistration,
  OpenClassSession,
  NewAttendeeParams,
  OpenClassSessionAttendance,
  CreateOpenClassBookingOrderParams,
  CreateOpenClassSessionParams,
  UpdateCertificateResultParams,
  UpdateOpenClassBookingOrderParams,
  UpdateOpenClassParams,
  UpdateOpenClassSessionParams,
  UpdateSessionAttendanceParams,
  ValidateOpenClassSessionParams,
} from './types/OpenClass';

const getOpenClasses = () => client.get<OpenClass[]>('/open-classes');

const getOpenClass = (id: OpenClass['id']) =>
  client.get<OpenClass>(`/open-classes/${id}`);

const getOpenClassRegistrations = (id: OpenClass['id']) =>
  client.get<OpenClassRegistration[]>(`/open-classes/${id}/registrations`);

const createOpenClass = (params: CreateOpenClassParams) =>
  client.post<OpenClass>('/open-classes', params);

const updateOpenClass = (id: OpenClass['id'], params: UpdateOpenClassParams) =>
  client.patch<OpenClass>(`/open-classes/${id}`, params);

const deleteOpenClass = (id: OpenClass['id']) =>
  client.delete(`/open-classes/${id}`);

const createSession = (
  openClassId: OpenClass['id'],
  params: CreateOpenClassSessionParams,
) =>
  client.post<OpenClassSession>(
    `/open-classes/${openClassId}/sessions`,
    params,
  );

const updateSession = (
  openClassId: OpenClass['id'],
  sessionId: OpenClassSession['id'],
  params: UpdateOpenClassSessionParams,
) =>
  client.patch<OpenClassSession>(
    `/open-classes/${openClassId}/sessions/${sessionId}`,
    params,
  );

const deleteSession = (
  openClassId: OpenClass['id'],
  sessionId: OpenClassSession['id'],
) => client.delete(`/open-classes/${openClassId}/sessions/${sessionId}`);

const validateSession = (params: ValidateOpenClassSessionParams) =>
  client.post<string[]>('/open-classes/sessions/validate', params);

export type OpenClassBookingOrderSummaryFilter = {
  from?: string;
  to?: string;
  subsidiaryCompanyIds?: number[];
  clientCompanyIds?: number[];
  courseIds?: number[];
  contactPersonIds?: number[];
  page?: number;
  pageSize?: number;
};

// This handler predates the shared ApiResponse convention and answers `paginationMeta`.
export type OpenClassBookingOrderSummariesResponse = {
  data: OpenClassBookingOrderSummary[];
  paginationMeta: PaginationMeta;
};

const getBookingOrderSummaries = (
  params?: OpenClassBookingOrderSummaryFilter,
) =>
  client.get<OpenClassBookingOrderSummariesResponse>(
    '/open-class-booking-orders/summaries',
    {
      params,
    },
  );

const getBookingOrderDetails = (id: OpenClassBookingOrder['id']) =>
  client.get<OpenClassBookingOrderDetails>(
    `/open-class-booking-orders/${id}/details`,
  );

const createBookingOrder = (params: CreateOpenClassBookingOrderParams) =>
  client.post<OpenClassBookingOrderDetails>(
    '/open-class-booking-orders',
    params,
  );

const createStandaloneBookingOrder = (
  params: CreateStandaloneOpenClassBookingOrderParams,
) =>
  client.post<OpenClassBookingOrderDetails>(
    '/open-class-booking-orders/standalone',
    params,
  );

const updateBookingOrder = (
  id: OpenClassBookingOrder['id'],
  params: UpdateOpenClassBookingOrderParams,
) =>
  client.patch<OpenClassBookingOrderDetails>(
    `/open-class-booking-orders/${id}`,
    params,
  );

// Created and attached in one transaction, or a rejected attach strands the attendee.
const addOrderParticipant = (
  orderId: OpenClassBookingOrder['id'],
  participant: AddOpenClassOrderParticipantParams,
) =>
  client.post<OpenClassBookingOrderDetails>(
    `/open-class-booking-orders/${orderId}/participants`,
    participant,
  );

const removeOrderParticipant = (
  orderId: OpenClassBookingOrder['id'],
  participantId: number,
) =>
  client.delete<OpenClassBookingOrderDetails>(
    `/open-class-booking-orders/${orderId}/participants/${participantId}`,
  );

const updateOrderParticipant = (
  orderId: OpenClassBookingOrder['id'],
  participantId: number,
  attendee: NewAttendeeParams,
) =>
  client.patch<OpenClassBookingOrderDetails>(
    `/open-class-booking-orders/${orderId}/participants/${participantId}`,
    attendee,
  );

const createParticipantRegistration = (
  orderId: OpenClassBookingOrder['id'],
  participantId: number,
  selection: OpenClassRegistrationSelectionParams,
) =>
  client.post<OpenClassBookingOrderDetails>(
    `/open-class-booking-orders/${orderId}/participants/${participantId}/registrations`,
    selection,
  );

const updateParticipantRegistration = (
  orderId: OpenClassBookingOrder['id'],
  participantId: number,
  registrationId: number,
  selection: OpenClassRegistrationSelectionParams,
) =>
  client.patch<OpenClassBookingOrderDetails>(
    `/open-class-booking-orders/${orderId}/participants/${participantId}/registrations/${registrationId}`,
    selection,
  );

const removeParticipantRegistration = (
  orderId: OpenClassBookingOrder['id'],
  participantId: number,
  registrationId: number,
) =>
  client.delete<OpenClassBookingOrderDetails>(
    `/open-class-booking-orders/${orderId}/participants/${participantId}/registrations/${registrationId}`,
  );

const uploadBesaccCertificate = (
  registrationId: OpenClassRegistration['id'],
  file: File,
) => {
  const data = new FormData();
  data.append('certificate', file);
  return client.put<OpenClassRegistration>(
    `/certificates/open-class-registrations/${registrationId}/besacc-certificate`,
    data,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

const deleteBesaccCertificate = (registrationId: OpenClassRegistration['id']) =>
  client.delete<OpenClassRegistration>(
    `/certificates/open-class-registrations/${registrationId}/besacc-certificate`,
  );

const getBesaccCertificate = (registrationId: OpenClassRegistration['id']) =>
  client
    .get<Blob>(`/certificates/open-class-registrations/${registrationId}`, {
      responseType: 'blob',
    })
    .then(({ data }) => data);

const updateCertificateResult = (
  id: OpenClassRegistration['id'],
  params: UpdateCertificateResultParams,
) =>
  client.patch<OpenClassRegistration>(
    `/open-class-registrations/${id}/certificate-result`,
    params,
  );

const updateSessionAttendance = (
  sessionId: OpenClassSession['id'],
  registrationId: OpenClassRegistration['id'],
  params: UpdateSessionAttendanceParams,
) =>
  client.put<OpenClassSessionAttendance>(
    `/open-class-sessions/${sessionId}/attendance/${registrationId}`,
    params,
  );

export default {
  deleteBesaccCertificate,
  getBesaccCertificate,
  addOrderParticipant,
  createBookingOrder,
  createStandaloneBookingOrder,
  createOpenClass,
  createSession,
  deleteOpenClass,
  deleteSession,
  getBookingOrderDetails,
  getBookingOrderSummaries,
  getOpenClass,
  getOpenClassRegistrations,
  getOpenClasses,
  updateOrderParticipant,
  updateParticipantRegistration,
  updateBookingOrder,
  updateCertificateResult,
  updateOpenClass,
  updateSession,
  updateSessionAttendance,
  uploadBesaccCertificate,
  createParticipantRegistration,
  removeOrderParticipant,
  removeParticipantRegistration,
  validateSession,
};
